'use client'

import * as React from 'react'

import type { FallbackCookies, Variant } from '../types'
import { makeVariantVisible, selectVariant } from '../scripts/head-scripts'
import { VariantSelectorScript } from '../scripts/body-scripts'
import { StaticVariant } from './static-variant'
import { convertToLean } from '../utils/convert-to-lean'
import { tw } from 'utils/classnames'

function StaticVariantsRendererInlineInternal({
  variants,
  fallbackCookies = {},
  renderingId,
  className,
}: {
  variants: Variant[]
  fallbackCookies?: FallbackCookies
  renderingId: string
  className?: string
}) {
  const hasMountedRef = React.useRef(false)

  React.useEffect(() => {
    if (!hasMountedRef.current) {
      const variantId = selectVariant({ variants: convertToLean(variants), fallbackCookies })
      makeVariantVisible(variantId, renderingId)
    }
    hasMountedRef.current = true
  }, [renderingId, variants, fallbackCookies])

  const staticVariantsData = React.useMemo(
    () => ({
      variants: convertToLean(variants),
      renderingId,
      fallbackCookies,
    }),
    [variants, renderingId, fallbackCookies],
  )

  return (
    <span
      id={renderingId}
      className={tw('inline-static-variant-renderer', className)}
      suppressHydrationWarning={true}
      data-variants-data={JSON.stringify(staticVariantsData)}
    >
      {variants.map(variant => (
        <StaticVariant key={variant.id} variant={variant} renderingId={renderingId} />
      ))}

      <VariantSelectorScript renderingId={renderingId} isInline />
    </span>
  )
}

export const StaticVariantsRendererInline = React.memo(StaticVariantsRendererInlineInternal)
